<template>
  <div class="vdog-game a-vg-d" v-visibility-change="changeBrowserTab">
    <!-- Main wrapper -->
    <div class="a-vg-wrapper">
      <!-- Inner wrapper for header and main part -->
      <div class="a-vg-wrapper--inner-main-wrapper">
        <div class="a-vg-b--header-wrap">
          <vdog-left-menu
            v-if="componentsVisible"
            :showLeftMenu="showLeftMenu"
            :showBettingHistory="showBettingHistory"
            :amount="amount"
            :currency="currency"
          />

          <vdog-betting-history
            v-show="showBettingHistory"
            :visible="showBettingHistory"
            :currency="currency"
            :currentRound="this.roundNo"
          />

          <!-- HEADER -->
          <vdog-header-container
            v-show="!showBettingHistory"
            @onError="onError($event)"
            :videoList="videoList"
            :seek="videoSeek"
            :roundLength="roundLength"
            :totalVideoLength="totalVideoLength"
            :isDemoMode="isDemoMode"
            :showFallenJackpots="showFallenJackpots"
            :showMatchResult="showMatchResult"
            :currency="currency"
            :previousMatch="previousMatch"
            :previousRoundNo="previousRoundNo"
            :roundResult="roundResult"
            :fallenJackpots="fallenJackpots"
          />

          <!-- Slide-out mobile ticket menu -->
          <!-- Bottom overlay, contains either text, or countdown timer for the next match. Here it gets tricky. -->
            <div v-show="!showBettingHistory" class="a-vg-header--overlay-bottom">
              <!-- Countdown timer -->
              <count-down-timer
                eventForTick="vdog_timer_tick"
                :secondsToStart="secondsToStart"
                :roundLength="roundLength"
                :roundNo="this.roundNo"
                :currentVGame="'GrayhoundRaces'"
              />
            </div>
        </div>
        <!-- MAIN -->
        <main v-show="!showBettingHistory">
          <ul class="a-vg-main--tabs">
            <li
              data-tab="match-1"
              @click="setActiveTab(0)"
              :class="{'active' :selectedTab === 0}"
            >{{$t('vdog_betting')}}</li>
            <li
              data-tab="match-2"
              @click="setActiveTab(1)"
              :class="{'active' :selectedTab === 1}"
              v-if="this.previousMatch"
            >{{$t('vdog_results')}}</li>
          </ul>
          <div class="a-vg-b--tab-wrap" style="min-height: 800px">
            <!-- Single tab (single match and all it's fields). All other tabs bellow are empty and will contain all the same elements as the first one -->
            <vdog-match
              id="match-1"
              class="a-vg-b--tab-panel"
              v-if="selectedTab === 0 && match"
              :class="{'active' :selectedTab === 0}"
              :isBettingEnabled="isBettingEnabled"
              :match="match"
            />

            <!-- Single tab Results tab -->
            <vdog-result-container
              id="match-2"
              class="a-vg-b--tab-panel results-tab"
              :class="{'active' :selectedTab === 1}"
              v-if="selectedTab === 1 && this.previousMatch && roundResult"
              :previousMatch="previousMatch"
              :roundResult="roundResult"
              :roundLastTenResult="roundLastTenResult"
            />
          </div>
        </main>
      </div>
      <!-- SIDEBAR -->
      <aside>
        <!-- Bet placing div bellow has 2 sections. First is a list of placed bets, second is the input of amount of money placed on a bet. -->
        <vdog-bet-placing
          class="a-vg-aside--bet-placing"
          ref="a-vg-aside--bet-placing"
          v-if="match && config"
          :config="config"
          :activeStake="match.activeStake"
          :isBettingEnabled="isBettingEnabled"
          :roundNo="roundNo"
          :amount="amount"
          :isMobileDesign="isMobile"
        />
        <!-- List of upcoming matches -->
        <vdog-played-tickets :currency="currency" />
      </aside>

      <vdog-floating-tab
        v-if="isMobile && match && componentsVisible && config"
        :amount="amount"
        :config="config"
        :showBettingHistory="showBettingHistory"
        :activeStake="match.activeStake"
        :isBettingEnabled="isBettingEnabled"
        :roundNo="roundNo"
        :currency="currency"
      />
    </div>

    <modal-connection-error
      v-if="showConnectionErrorModal"
      @close="showConnectionErrorModal = false"
    />
    <modal-device-orientation v-if="showModal" @close="showModal = false" />

    <vdog-modal-confirm
      v-if="match && match.activeStake && openConfirmModal && config"
      :eventData="match"
      :config="config"
      :isBettingEnabled="isBettingEnabled"
      :numberOfTicketsInList="numberOfTicketsInList"
    >
      <p slot="teams" class="teams">{{$t(match.activeStake.tableName)}}</p>
      <p class="bet-type" slot="bet-type">
        <span
          v-for="(item, index) in extractConfirmSubtitleByStake(match.activeStake)"
          :key="index"
        >{{ $t(item.title) }} {{item.subtitle}}</span>
      </p>
    </vdog-modal-confirm>
  </div>
</template>

<script>
import api from '@/services/grayhound/api.service.js'
import { eventBus } from '@/main.js'
import EventConstants from '../../constants/Events'
import Vue from 'vue'
import VDogMatch from '@/components/grayhound/match/VDogMatch.vue'
import VDogBetPlacing from '@/components/grayhound/bet-placing/VDogBetPlacing.vue'
import VDogResultContainer from '@/components/grayhound/results/VDogResultContainer.vue'

import VDogPlayedTickets from '@/components/grayhound/ticket/VDogPlayedTickets.vue'
import VDogFloatingButton from '@/components/grayhound/floating-tab/VDogFloatingTab.vue'
import VDogLeftMenuContainer from '@/components/grayhound/left-menu/VDogLeftMenuContainer.vue'
import VDogBettingHistory from '@/components/grayhound/history/VDogBettingHistory.vue'
import VDogConfirmScreen from '@/components/grayhound/confirm/VDogConfirmScreen.vue'
import VDogHeaderContainer from '@/components/grayhound/header/VDogHeaderContainer.vue'

import vuexStore from '../../store/vuexStore'

import MobileDevice from '@/mixins/MobileDevice'
// import FullScreen from '@/mixins/FullScreen.js'
import HandleResize from '@/mixins/HandleResize.js'
import ModalOnLandscape from '@/mixins/ModalOnLandscape.js'
import visibility from 'vue-visibility-change'
import Elements from '@/mixins/VDogElements.js'
import VDogHelper from '@/mixins/VDogHelper.js'
import BettingHistoryShow from '@/mixins/BettingHistoryShowVdog.js'
import NetworkHelper from '@/mixins/NetworkHelper.js'
import GrayHoundData from './GrayHoundData.js'

import generalMenuSound from '../../assets/audio/general_menu.mp3'

Vue.use(visibility)
export default {
  mixins: [MobileDevice,
    Elements,
    HandleResize,
    ModalOnLandscape,
    VDogHelper, NetworkHelper,
    BettingHistoryShow
  ],
  components: {
    'vdog-match': VDogMatch,
    'vdog-bet-placing': VDogBetPlacing,
    'vdog-header-container': VDogHeaderContainer,
    'vdog-played-tickets': VDogPlayedTickets,
    'vdog-floating-tab': VDogFloatingButton,
    'vdog-betting-history': VDogBettingHistory,
    'vdog-modal-confirm': VDogConfirmScreen,
    'vdog-left-menu': VDogLeftMenuContainer,
    'vdog-result-container': VDogResultContainer
  },
  props: {
    isShowing: Boolean,
    isDemoMode: {
      type: Boolean,
      default: false
    },
    componentsVisible: Boolean,
    amount: {
      type: Number,
      required: true
    },
    currency: {
      type: String,
      required: true
    }
  },
  beforeDestroy () {
    this.isVisible = false
    document.removeEventListener('fullscreenchange', this.isFullScreen)
    eventBus.$off(EventConstants.VDOG_CONFIRM_MODAL_CLOSE, this.closeConfirmModal)
    eventBus.$off(EventConstants.VDOG_SELECTED_STAKE, this.selectActiveStake)
    eventBus.$off(EventConstants.VDOG_CHANGE_PICKS_LENGTH, this.updateNumberOfTickets)
    eventBus.$off(EventConstants.VDOG_SHOW_JACKPOT_VALUE, this.onEventShowJackpot)
    eventBus.$off('vdog_timer_tick', this.onEventTimerTick)
    eventBus.$off(EventConstants.VSOCCER_SWIPE_ACTION, this.handleResize)
    eventBus.$off(EventConstants.CHANGE_FULLSCREEN_VARIABLE, this.setFSVariable)
    eventBus.$off(EventConstants.CHECK_IF_MODAL, this.modalOnMobileLandscape)

    if (this.isMobile) {
      window.removeEventListener('orientationchange', this.modalOnMobileLandscape)
    }
  },
  data () {
    return GrayHoundData.data()
  },
  methods: {
    setActiveTab (tab) {
      if (this.hasAudioEnabled) {
        var audio = new Audio(generalMenuSound)
        audio.play()
      }
      this.selectedTab = tab
      if (tab === 1) {
        // todo reset
        if (this.match) {
          this.match.activeStake = null
        }
        eventBus.onResetVDog()
      }
    },
    onError (event) {
      this.getRound()
    },
    changeBrowserTab (event, hidden) {
      if (!hidden) {
        this.getRound(true)
      }
    },
    setFSVariable (data) {
      this.fullScreenActive = data
    },
    updateNumberOfTickets: function (numberOfTicketsInList) {
      this.numberOfTicketsInList = numberOfTicketsInList
    },
    closeConfirmModal: function () {
      this.openConfirmModal = false
    },
    setBettingTimerEnabled (isBettingEnabled) {
      this.isBettingEnabled = isBettingEnabled
    },
    selectActiveStake () {
      this.openConfirmModal = true
    },
    setSwipe (data) {
      eventBus.$emit(EventConstants.VDOG_SWIPE_ACTION, data)
    },
    onEventToggleLeftMenu (isOpen) {
      this.showLeftMenu = isOpen
    },
    onEventShowBettingHistory (isOpen) {
      this.showBettingHistory = isOpen
    },
    onEventShowJackpot (data) {
      let prepareData = this.getPreparedDataJackpot(data)
      this.fallenJackpots = prepareData.fallenJackpots
      this.showFallenJackpots = prepareData.showFallenJackpots
    },
    onEventShowConnectionErrorModal (isOpen) {
      this.showConnectionErrorModal = isOpen
      setInterval(function () { location.reload() }, 5000)
    },
    onNewRoundVDog () {
      setTimeout(() => {
        this.getServerTime(() => {
          this.getRound()
          if (this.match) {
            this.match.activeStake = null
          }
          eventBus.showJackpotValueVDog(null)
          this.videoList = this.videoListTemp
          this.videoSeek = 0
          this.videoListTemp = []
        })
      }, 1000)
    },
    onGetResultVDogForCurrentMatch (roundNo, fullVideo) {
      if (roundNo > 0) {
        this.isRoundResultCalled = true
        api.getRoundResult(roundNo).subscribe(data => {
          if (!data.VideoUrl) {
            return
          }
          if (this.secondsToStart < 5) {
            this.videoListTemp = [data.VideoUrl]
          }
          this.totalVideoLength = (data.TotalLength / 1000) || 0
          let passSec = this.roundLength - this.secondsToStart
          let videoPositionPercentage = ((passSec * 1000) / data.TotalLength) * 100
          let videoPercentage = 70
          if (fullVideo) {
            videoPercentage = 100
          }
          if (videoPositionPercentage < videoPercentage) {
            this.videoList = [data.VideoUrl]
            this.videoSeek = passSec
            eventBus.onRefreshActiveBetsVDog()
          } else {
            this.getPreviousRound(this.previousRoundNo)
            this.videoSeek = 0
            this.videoList = []
          }
        }, err => {
          this.isRoundResultCalled = false
          eventBus.onApiErrorVDog(err)
        })
      }
    },
    getResults () {
      api.getResults().subscribe(data => {
        this.roundLastTenResult = data
      }, eventBus.onApiErrorVDog)
    },
    onEventTimerTick: function (secondsToStart) {
      this.secondsToStart = secondsToStart
      if (secondsToStart <= this.noBetsPeriod) {
        this.setBettingTimerEnabled(false)
      }
      if (secondsToStart <= 2) {
        // important check this
        if (!this.isRoundResultCalled) {
          this.isRoundResultCalled = true
          this.onGetResultVDogForCurrentMatch(this.roundNo)
        }
      }
      if (secondsToStart <= 0) {
        this.isRoundResultCalled = false
        this.onNewRoundVDog()
      }
    },
    onEventShowMatchResult (data) {
      if (data) {
        this.getPreviousRound(this.previousRoundNo, () => {
          if (this.roundResult) {
            this.showMatchResult = data
          }
        })
      } else {
        this.showMatchResult = data
      }
    },
    getRound (isFullVideo) {
      eventBus.showJackpotValueVDog(null)
      api.getRound().subscribe(data => {
        this.roundNo = data.RoundNo
        this.previousRoundNo = data.PreviousRoundNo
        this.match = this.getPreparedDataFromMatch(data, false)
        this.onGetResultVDogForCurrentMatch(data.PreviousRoundNo, isFullVideo)
      }, eventBus.onApiErrorVDog)
    },
    getPreviousRound (roundNo, callback) {
      api.getRound(roundNo).subscribe(data => {
        this.previousMatch = this.getPreparedDataFromMatch(data, true)
        api.getRoundResult(roundNo).subscribe(data => {
          this.roundResult = data
          if (callback != null) {
            callback()
          }
        }, eventBus.onApiErrorVDog)
      }, eventBus.onApiErrorVDog)
      this.getResults()
    },
    getConfig: function () {
      api.getConfig().subscribe(data => {
        if (data === null) {
          ++this.getConfigCounter
          if (this.getConfigCounter === 6) return
          setTimeout(() => { this.getConfig() }, 1000)
        } else {
          this.config = data
          this.getConfigCounter = 0
          api.getBalance()
        }
      }, eventBus.onApiErrorVDog)
    },
    getServerTime (callback) {
      api.getServerTime().subscribe(data => {
        this.secondsToStart = data.SecondsToStart
        this.roundLength = data.RoundLength
        this.noBetsPeriod = data.NoBetsPeriod
        this.isBettingEnabled = data.IsBettingEnabled
        if (this.secondsToStart <= 0) {
          setTimeout(() => {
            this.getServerTime(callback)
          }, 500)
          return
        } else {
          if (callback) {
            callback()
          }
        }
        eventBus.secondToStartVDog(data.SecondsToStart)
      }, eventBus.onApiErrorVDog)
    }
  },
  created: function () {
    this.isVisible = true
    this.elementVGames = this.$parent.$parent.$refs.VGames
    eventBus.$on('vdog_timer_tick', this.onEventTimerTick)
    this.getServerTime()
    this.getConfig()
    this.getRound()
    window.addEventListener('resize', this.handleResize)
    eventBus.$on(EventConstants.VDOG_CHANGE_PICKS_LENGTH, this.updateNumberOfTickets)
    eventBus.$on(EventConstants.VDOG_SELECTED_STAKE, this.selectActiveStake)
    eventBus.$on(EventConstants.VDOG_CONFIRM_MODAL_CLOSE, this.closeConfirmModal)
    eventBus.$on(EventConstants.VDOG_TOGGLE_LEFT_MENU, this.onEventToggleLeftMenu)
    eventBus.$on(EventConstants.VDOG_SHOW_MAINTENANCE, this.onEventShowConnectionErrorModal)
    eventBus.$on(EventConstants.VDOG_SHOW_API_ERROR, this.onEventShowConnectionErrorModal)
    eventBus.$on(EventConstants.VDOG_SHOW_MATCH_RESULT, this.onEventShowMatchResult)
    eventBus.$on(EventConstants.VDOG_SHOW_BETTING_HISTORY, this.onEventShowBettingHistory)
    eventBus.$on(EventConstants.VDOG_SHOW_JACKPOT_VALUE, this.onEventShowJackpot)
    eventBus.$on(EventConstants.VSOCCER_SWIPE_ACTION, this.handleResize)
    eventBus.$on(EventConstants.CHANGE_FULLSCREEN_VARIABLE, this.setFSVariable)
    eventBus.$on(EventConstants.CHECK_IF_MODAL, this.modalOnMobileLandscape)

    if (this.isMobile) {
      window.addEventListener('orientationchange', this.modalOnMobileLandscape)
    }
    if (this.isShowing) {
      Vue.nextTick(() => {
        this.elementVGames.style.marginLeft = ''
        this.elementVGames.style.marginRight = ''
      })
    }
    eventBus.onGameScrollUnock('true')
    this.handleResize()
  },
  mounted: function () {
    setTimeout(function () { eventBus.onVDogFloatingAction(false) }, 500)
    this.$nextTick(eventBus.onVDogComponentLoaded)
    this.$watch(
      () => vuexStore.state.hasAudioEnabled,
      () => {
        this.hasAudioEnabled = vuexStore.state.hasAudioEnabled
      }
    )
  },
  watch: {
    isShowing: function (newVal, oldVal) {
      if (newVal) {
        this.handleResize()
        Vue.nextTick(() => {
          this.elementVGames.style.marginLeft = '0px'
          this.elementVGames.style.marginRight = '0px'
        })
      } else {
        Vue.nextTick(() => {
          this.elementVGames.style.marginLeft = ''
          this.elementVGames.style.marginRight = ''
        })
      }
    }
  },
  computed: {
    isMobile: function () {
      return this.isMobileDevice()
    }
  }
}
</script>
<style lang="scss">
@import "../../assets/grayhound/css/style.scss";
</style>
